import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import axios from "axios";
import { useLocation } from "react-router-dom";
import CrossIcon from "../assets/icons/ProcessingQueueIcons/Close.png";
import DownIcon from "../assets/icons/ProcessingQueueIcons/DownIcon.png";
import FileIcon from "../assets/icons/ProcessingQueueIcons/FileIcon.png";
import TickIcon from "../assets/icons/icons_tick.png";
import {
  clearQueue,
  removeNewFile,
  setDuplicates,
  setNewFiles,
  setStatus,
  updateFile,
  setWorkerRunning,
  markJobConsumed,
} from "../redux/uploadQueueSlice";
import { toast } from "react-toastify";
import { showSuccessToast } from "../utils/toastUtils";

const API_BASE = process.env.REACT_APP_API_URL;
const token = localStorage.getItem("access_token");

export default function UploadQueue() {
  const location = useLocation();
  const showQueue = location.pathname === "/user/learning-space";
  const dispatch = useDispatch();
  const uploadState = useSelector((s) => s.uploadQueue);

  const queue = uploadState.queue || [];
  const duplicates = uploadState.duplicates || [];
  const newFiles = uploadState.files || [];
  //If new file are updated then they are received here
  console.log("newFiles", newFiles);

  const [visible, setVisible] = useState(true);
  const [minimized, setMinimized] = useState(false);
  const [hiddenUploads, setHiddenUploads] = useState(new Set());
  const [processingAction, setProcessingAction] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState([]);
  const [uploadingDuplicates, setUploadingDuplicates] = useState([]);
  const dragRef = useRef(null);
  const dragging = useRef(false);
  const dragStart = useRef({ x: 0, y: 0 });
  const [position, setPosition] = useState({ bottom: 20, right: 20 });

  const clamp = (num, min, max) => Math.min(Math.max(num, min), max);

  // ------------------------------------------------
  // DUPLICATE CHECK (RUNS ONLY ONCE PER JOB)
  // ------------------------------------------------
  const checkFileDuplicates = async (files, meta, jobId) => {
    const token = localStorage.getItem("access_token");

    try {
      const fd = new FormData();
      files.forEach((file) => fd.append("files", file));
      fd.append("mode", meta.mode.toLowerCase());
      fd.append("description", meta.description || "");

      const res = await axios.post(
        `${API_BASE}/uploaded-files/checkDuplicates`,
        fd,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // DUPLICATES
      const duplicatesWithFile = (res.data.duplicates || []).map((dup) => ({
        ...dup,
        file: files.find((f) => f.name === dup.originalname),
      }));

      dispatch(setDuplicates(duplicatesWithFile));

      // NEW FILES
      const newFilesFromServer = (res.data.new_files || []).map((nf) => ({
        id: nf.file_hash,
        name: nf.originalname,
        file_hash: nf.file_hash,
        size: nf.size,
        file: files.find((f) => f.name === nf.originalname),
        status: "pending",
        progress: 0,
        uploading: false,
        finalized: false,
        mode: nf.upload_mode,
        description: nf.file_description,
      }));

      dispatch(setNewFiles(newFilesFromServer));

      // 🔐 MARK JOB AS CONSUMED (CRITICAL)
      dispatch(markJobConsumed(jobId));
    } catch (err) {
      console.error("Duplicate check failed", err);
    }
  };

  // ------------------------------------------------
  // FILE UPLOAD (ROUTE SAFE)
  // ------------------------------------------------
  const uploadNewFiles = async (filesToUpload) => {
    const token = localStorage.getItem("access_token");

    for (const fileObj of filesToUpload) {
      // 🛑 HARD STOP — survives navigation
      if (fileObj.uploading || fileObj.status === "success") continue;

      try {
        dispatch(
          updateFile({
            id: fileObj.id,
            status: "uploading",
            uploading: true,
          })
        );

        const fd = new FormData();
        fd.append("files", fileObj.file);
        fd.append("file_hash", fileObj.file_hash);
        fd.append("mode", fileObj.mode);
        fd.append("description", fileObj.description);

        const response = await axios.post(
          `${API_BASE}/uploaded-files/upload`,
          fd,
          {
            headers: { Authorization: `Bearer ${token}` },
            onUploadProgress: (e) => {
              const progress = Math.round((e.loaded * 100) / e.total);
              dispatch(updateFile({ id: fileObj.id, progress }));
            },
          }
        );

        dispatch(
          updateFile({
            id: fileObj.id,
            status: "success",
            uploading: false,
            finalized: true, // 🔐 IMPORTANT
            backendData: response.data.uploaded_files[0],
          })
        );

        setTimeout(() => {
          dispatch(removeNewFile(fileObj.id));
        }, 5000);
      } catch (err) {
        dispatch(
          updateFile({
            id: fileObj.id,
            status: "failed",
            uploading: false,
          })
        );
      }
    }
  };

  // ------------------------------------------------
  // RUN DUPLICATE CHECK (ONCE)
  // ------------------------------------------------
  useEffect(() => {
    if (!queue.length) return;

    const job = queue[0];

    // 🔐 HARD STOP — job already processed
    if (uploadState.consumedJobs[job.jobId]) return;

    // Prevent re-run while data exists
    if (newFiles.length > 0 || duplicates.length > 0) return;

    if (job.type === "files") {
      checkFileDuplicates(job.items, job.meta, job.jobId);
    }
  }, [queue, uploadState.consumedJobs, newFiles.length, duplicates.length]);

  // ------------------------------------------------
  // UPLOAD PENDING FILES (SAFE ON NAVIGATION)
  // ------------------------------------------------
  useEffect(() => {
    if (uploadState.workerRunning) return;
    if (!newFiles.length) return;

    const pendingFiles = newFiles.filter(
      (f) => f.status === "pending" && !f.uploading
    );

    if (!pendingFiles.length) return;

    dispatch(setWorkerRunning(true));

    (async () => {
      await uploadNewFiles(pendingFiles);
      dispatch(setWorkerRunning(false));
    })();
  }, [newFiles, uploadState.workerRunning]);

  // -----------------------------
  // MOVE WINDOW
  // -----------------------------
  const handleMouseDown = (e) => {
    if (!minimized) return;
    if (e.target.closest("button")) return;

    dragging.current = true;
    dragStart.current = { x: e.clientX, y: e.clientY };
    document.body.style.userSelect = "none";
  };

  const handleMouseMove = (e) => {
    if (!dragging.current) return;

    const dy = e.clientY - dragStart.current.y;
    setPosition((prev) => {
      const boxHeight = dragRef.current?.offsetHeight || 40;
      const maxBottom = window.innerHeight - boxHeight - 10;
      const minBottom = 10;

      const newBottom = clamp(prev.bottom - dy, minBottom, maxBottom);

      return { ...prev, bottom: newBottom };
    });

    dragStart.current = { x: e.clientX, y: e.clientY };
  };

  const handleMouseUp = () => {
    dragging.current = false;
    document.body.style.userSelect = "auto";
  };

  useEffect(() => {
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, []);

  // const uploadingList = useMemo(() => {
  //   if (Array.isArray(newFiles) && newFiles.length) return newFiles;

  //   let arr = [];
  //   for (const job of queue) {
  //     if (job.type === "files") {
  //       arr = arr.concat(
  //         job.items.map((f) => ({
  //           id: f.name,
  //           name: f.name,
  //           progress: 0,
  //           status: "pending",
  //         }))
  //       );
  //     }
  //   }
  //   return arr;
  // }, [newFiles, queue]);

  const uploadingList = useMemo(() => {
    // If backend gave new files → show them
    if (newFiles?.length > 0) return newFiles;

    // If user continued duplicates → show them
    if (uploadingDuplicates?.length > 0) return uploadingDuplicates;

    // Otherwise → nothing should show
    return [];
  }, [newFiles, uploadingDuplicates]);

  // console.log("uploadingList", uploadingList);

  const saveDuplicateToDB = async (fileObj, file) => {
    if (!fileObj || !file) {
      console.error("fileObj or file missing", { fileObj, file });
      return;
    }

    const formData = new FormData();
    formData.append("files", file);
    formData.append("file_hash", fileObj.file_hash);
    formData.append("originalname", fileObj.originalname);
    formData.append("mode", fileObj.upload_mode || fileObj.mode);
    formData.append("description", fileObj.description || "");

    try {
      const res = await axios.post(
        `${API_BASE}/uploaded-files/saveDuplicateFiles`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      return res.data;
    } catch (err) {
      console.error("Failed to save duplicate:", err);
      throw err;
    }
  };
  const handleContinueDuplicate = async (fileObj) => {
    if (!fileObj?.file) return;

    try {
      // 1️⃣ Upload duplicate file
      await saveDuplicateToDB(fileObj, fileObj.file);

      // 2️⃣ Remove from duplicate queue (UI)
      dispatch(
        setDuplicates(
          duplicates.filter((d) => d.file_hash !== fileObj.file_hash)
        )
      );

      // 3️⃣ MARK FILE AS FINALIZED 🔐 (MOST IMPORTANT)
      dispatch(
        updateFile({
          id: fileObj.file_hash,
          status: "success",
          finalized: true,
        })
      );

      toast.success(`"${fileObj.originalname}" uploaded successfully`);
    } catch (err) {
      toast.error(`Failed to upload "${fileObj.originalname}"`);
    }
  };

  const handleContinueAll = async () => {
    if (!duplicates.length) return;

    for (const fileObj of duplicates) {
      if (!fileObj.file) continue;

      try {
        await saveDuplicateToDB(fileObj, fileObj.file);

        // MARK EACH FILE AS FINALIZED 🔐
        dispatch(
          updateFile({
            id: fileObj.file_hash,
            status: "success",
            finalized: true,
          })
        );
      } catch (err) {
        toast.error(`Failed to upload "${fileObj.originalname}"`);
      }
    }

    // Clear duplicate queue after processing all
    dispatch(setDuplicates([]));

    toast.success("All duplicate files uploaded successfully");
  };

  // -----------------------------
  // UI Rendering
  // -----------------------------

  // if (!visible) return null;
  // if (!duplicates.length && uploadingList.length === 0) return null;

  const totalCount = duplicates.length + uploadingList.length;

  return (
    <>
      {showQueue && (
        <div
          className="upload-queue-box position-fixed shadow-lg refresh-prompts"
          ref={dragRef}
          onMouseDown={handleMouseDown}
          style={{
            bottom: position.bottom,
            right: position.right,
            width: minimized ? 220 : 360,
            maxWidth: "90vw", // 👈 added for responsiveness
            borderRadius: 10,
            background: "rgba(255,255,255,0.12)",
            backdropFilter: "blur(8px)",
            zIndex: 9999,
          }}
        >
          {/* HEADER */}
          <div className="d-flex justify-content-between align-items-center p-2 border-bottom">
            <strong>Uploading {totalCount} Files</strong>

            <div className="d-flex gap-1">
              <button
                className="btn btn-sm btn-light"
                onClick={() => setMinimized((p) => !p)}
              >
                <img src={DownIcon} width={14} />
              </button>
            </div>
          </div>

          {/* CONTENT */}
          {!minimized && (
            
            <div style={{ maxHeight: 350, overflowY: "auto" }}>
              {/* DUPLICATES */}
              {duplicates.length > 0 && (
                <>
                  <div className="d-flex justify-content-between align-items-center p-2 pb-1">
                    <h6
                      className="m-0 refresh-prompts"
                      style={{ fontSize: "16px" }}
                    >
                      Duplicate Files
                    </h6>

                    <button
                      type="button"
                      className=" bg-secondary"
                      style={{
                        padding: "4px 8px",
                        // backgroundColor: isAdded ? "#cccccc" : "var(--secondary-color)",
                        color: "#fff",
                        border: "none",
                        borderRadius: "6px",
                        cursor: "pointer",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                      onClick={handleContinueAll}
                      disabled={processingAction || duplicates.length === 0}
                    >
                      Continue All
                    </button>
                  </div>

                  {duplicates.length === 0 && (
                    <div className="px-3 pb-2 queuetext">No duplicates</div>
                  )}

                  {duplicates.map((d, i) => (
                    <div
                      key={i}
                      className="p-2 border mx-2 mb-1 d-flex justify-content-between align-items-center"
                    >
                      <div className="d-flex gap-2 ">
                        <img src={FileIcon} width={14} />
                        <span
                          className="text-truncate"
                          style={{ maxWidth: 200, fontSize: "14px" }}
                        >
                          {d.originalname}
                        </span>
                      </div>

                      <div>
                        <button
                          className="btn  btn-sm me-1 border"
                          onClick={() =>
                            dispatch(
                              setDuplicates(
                                duplicates.filter(
                                  (x) => x.file_hash !== d.file_hash
                                )
                              )
                            )
                          }
                          disabled={processingAction}
                        >
                          <img src={CrossIcon} width={14} />
                        </button>

                        <button
                          className="btn  btn-sm border"
                          onClick={() => handleContinueDuplicate(d)}
                          disabled={processingAction}
                        >
                          <img src={TickIcon} width={14} />
                        </button>
                      </div>
                    </div>
                  ))}
                </>
              )}

              {/* UPLOADING */}
              {uploadingList.length > 0 && (
                <h6 className="p-2 pt-3" style={{ fontSize: "16px" }}>
                  Uploading Files
                </h6>
              )}

              {uploadingList.map((f) => (
                <div
                  key={f.id}
                  className="p-2 border mx-2 mb-1 d-flex justify-content-between align-items-center"
                >
                  <div className="d-flex gap-2">
                    <img src={FileIcon} width={16} />
                    <span
                      className="text-truncate refresh-prompts"
                      style={{ maxWidth: 200, fontSize: "14px" }}
                    >
                      {f.name}
                    </span>
                  </div>

                  <div
                    style={{ width: 40 }}
                    className="d-flex justify-content-center"
                  >
                    {f.status === "pending" && (
                      <div
                        className="spinner-border text-primary"
                        style={{ width: "16px", height: "16px" }}
                      ></div>
                    )}

                    {f.status === "success" && (
                      <img src={TickIcon} width={18} />
                    )}

                    {f.status === "failed" && (
                      <span className="text-danger">Failed</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </>
  );
}
